import {
  exactPin,
  missionTemplateReadResult,
  pinFields,
  samePin,
  type ExactPin,
} from '../template/consumer/contract';
import type { MissionTemplateObservation } from '../template/consumer/reader';

export type WorkspaceStatus = MissionTemplateObservation['status'] | 'LOADING' | 'NOT_CONNECTED';
export type WorkspaceProjection = Readonly<{
  status: WorkspaceStatus;
  statusText: string;
  identityLabel: string;
  identity: readonly Readonly<{ label: string; value: string }>[];
  templateId: string;
  version: string;
  schema: string;
  authority: 'NON_AUTHORITY';
  sourceText: string | null;
}>;

const labels = {
  kind: 'Kind',
  id: 'Template Identity',
  version: 'Version',
  schemaVersion: 'Schema',
  canonicalizationVersion: 'Canonicalization',
  digest: 'Digest',
};
const explanations: Record<WorkspaceStatus, string> = {
  VERIFIED: '读取边界已核验',
  MISSING_REFERENCE: '未找到请求的精确内容引用',
  SOURCE_UNAVAILABLE: '来源内容当前不可读取',
  INTEGRITY_MISMATCH: '内容完整性核验不匹配',
  IDENTITY_MISMATCH: '来源读取报告身份不匹配',
  UNSUPPORTED_SCHEMA: '当前读取不支持此 Schema',
  UNSUPPORTED_CANONICALIZATION: '当前读取不支持此规范化版本',
  UNSUPPORTED_REPRESENTATION: '当前读取不支持此表示形式',
  ERROR: '本地读取错误，阶段未分类',
  LOADING: '正在读取请求对象',
  NOT_CONNECTED: '未发起读取 · 当前环境未绑定读取来源',
};

/** Display projection only. No stage, Unknown, recommendation or runtime inference. */
export function projectMissionWorkspace(
  query: ExactPin,
  observation: MissionTemplateObservation | Readonly<{ status: 'LOADING' | 'NOT_CONNECTED' }>,
): WorkspaceProjection {
  const requested = exactPin(query);
  let status: WorkspaceStatus = observation.status;
  let sourceText: string | null = null;
  try {
    if ('query' in observation) {
      if (!samePin(requested, observation.query)) throw new Error('Response pin mismatch');
      const accepted = missionTemplateReadResult(
        observation.status,
        observation.query,
        observation.snapshot,
      );
      if (accepted.status === 'VERIFIED') {
        const text = new TextDecoder('utf-8', { fatal: true }).decode(
          new Uint8Array(accepted.snapshot.canonicalContent),
        );
        const envelope: unknown = JSON.parse(text);
        const fields = [...pinFields.filter((field) => field !== 'digest'), 'body'];
        if (!envelope || typeof envelope !== 'object' || Array.isArray(envelope))
          throw new Error('Content envelope required');
        const content = envelope as Record<string, unknown>;
        if (
          Object.keys(content).length !== fields.length ||
          fields.some((field) => !Object.hasOwn(content, field)) ||
          pinFields.some((field) => field !== 'digest' && content[field] !== requested[field]) ||
          !content.body ||
          typeof content.body !== 'object' ||
          Array.isArray(content.body)
        )
          throw new Error('Content envelope mismatch');
        // Preserve exact decoded text. No recanonicalization, hashing or domain normalization.
        sourceText = text;
      }
    }
  } catch {
    status = 'ERROR';
  }
  return Object.freeze({
    status,
    statusText: explanations[status],
    identityLabel: status === 'VERIFIED' ? '已核验读取对象' : '请求对象',
    identity: Object.freeze(
      pinFields.map((key) => Object.freeze({ label: labels[key], value: requested[key] })),
    ),
    templateId: requested.id,
    version: requested.version,
    schema: requested.schemaVersion,
    authority: 'NON_AUTHORITY',
    sourceText,
  });
}
